import { Alert, Box, Button, Container, Drawer, Grid2, IconButton, Link, Menu, MenuItem, Modal, Stack, TextField, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBackIosNew'
import MoreHoriz from '@mui/icons-material/MoreHoriz'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import CloseIcon from '@mui/icons-material/Close'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';

const BUILD_URL = 'https://coursedata.opengolfsim.com/simulator/urp-test/webgl/v15';

const drawerWidth = 320;

const CONFIG = {
  loaderUrl: `${BUILD_URL}/ogscore.loader.js`,
  dataUrl: `${BUILD_URL}/ogscore.data.br`,
  frameworkUrl: `${BUILD_URL}/ogscore.framework.js.br`,
  codeUrl: `${BUILD_URL}/ogscore.wasm.br`,
  // streamingAssetsUrl: "StreamingAssets",
  // companyName: "OpenGolfSim LLC",
  // productName: "OGSUniversal",
  // productVersion: "1.6.2",
  matchWebGLToCanvasSize: false
};

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    flexGrow: 1,
    // padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    variants: [
      {
        props: ({ open }) => open,
        style: {
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
          }),
          marginLeft: drawerWidth,
        },
      },
    ],
  }),
);

export default function SimulatorWebGL() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { state } = location;
  const frameRef = useRef();
  const [isLoaded, setIsLoaded] = useState(false);

  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(true);
  const [shotQueue, setShotQueue] = useState([]);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const open = useMemo(() => {
    return Boolean(menuAnchorEl);
  }, [menuAnchorEl]);

  const [testShotData, setTestShotData] = useState({
    ballSpeed: '145',
    horizontalLaunchAngle: '0',
    verticalLaunchAngle: '11',
    spinSpeed: '2000',
    spinAxis: '0'
  });

  const handleClose = () => {
    setMenuAnchorEl(null);
  }
  const setupGame = useCallback(() => {
    if (!state?.gameData) {
      console.log('no gameData was passed!');
      return;
    }
    frameRef.current.src = state.gameData.url;
  }, [state.gameData]);

  const openHelp = () => {
    window.open('https://help.opengolfsim.com/web');
    handleClose();
  }

  const sendMessage = useCallback((message) => {
    const w = frameRef.current.contentWindow;
    if (!w) {
      console.log('no window');
      return;
    }
    w.postMessage(message, '*'); // or specify the target origin
  }, []);
  
  const handleTestShot = useCallback(() => {
    const numbers = Object.entries(testShotData).reduce((old, [key, val]) => {
      return {
        ...old,
        [key]: parseFloat(val)
      }
    }, {});
    console.log('test shot', numbers);
    sendMessage({
      type: 'shot',
      shot: numbers
    });
  }, [testShotData, sendMessage]);


  const handleChangeShot = (property, value) => {
    setTestShotData(old => ({ ...old, [property]: value.replace(/[^0-9.-]+/g, '') }));
  }


  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  }
  const exitSimulator = () => {
    navigate('/account/simulator');
    handleClose();
  }

  useEffect(() => {
    if (shotQueue.length > 0) {
      setShotQueue([...shotQueue]);
    }
  }, [shotQueue]);


  const handleMessage = useCallback((event) => {
    if (!event.data?.type) {
      return;
    }
    switch (event.data.type) {
      case 'exit':
        exitSimulator();
        break;
      case 'ready':
        sendMessage({
          type: 'setup',
          setupData: state.setupData,
          gameConfig: {},
          gameData: state.gameData      
        });
        break;
      default:
        console.log('unhandled event', event.data);
        break;
    }
  }, [state?.setupData, state?.gameData, sendMessage, exitSimulator]);

  useEffect(() => {

    window.addEventListener('message', handleMessage);
    
    let iframeRef = frameRef.current;
    if (iframeRef) {
      setupGame();
    }
    return () => {
      if (iframeRef) iframeRef.href = 'about:blank';
      window.removeEventListener('message', handleMessage);
    }
  }, []);


  if (!state?.setupData) {
    return (
      <Container maxWidth="sm" sx={{ pt: 5 }}>
        <Alert
          severity="error"
          action={<Button color="inherit" onClick={() => navigate('/account/simulator')}>Return to Dashboard</Button>}
        >
          Error: No course was launched!
        </Alert>
      </Container>
    )
  }

  return (
    <Box sx={{ position: 'relative', backgroundColor: '#333', height: '100vh', flex: 1, overflow: 'hidden' }}>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant="persistent"
        anchor="left"
        open={settingsDrawerOpen}
      >
        <Stack spacing={2} sx={{ p: 3 }}>
          <Stack direction="row">
            <IconButton onClick={exitSimulator}>
              <ArrowBackIcon />
            </IconButton>
            <IconButton id="more-menu-button" sx={{ ml: 'auto' }} onClick={handleMenuOpen}>
              <MoreHoriz />
            </IconButton>
            <Menu
              id="more-menu"
              anchorEl={menuAnchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              slotProps={{
                list: {
                  'aria-labelledby': 'more-menu-button',
                },
              }}
            >
              <MenuItem onClick={openHelp}>Help</MenuItem>
              <MenuItem onClick={exitSimulator}>Exit Simulator</MenuItem>
            </Menu>
          </Stack>

          <TextField
            fullWidth={true}
            label="Ball Speed (MPH)"
            value={testShotData.ballSpeed}
            onChange={(e) => handleChangeShot('ballSpeed', e.target.value)}
          />
          <TextField
            fullWidth={true}
            label="Horizontal Launch Angle (Degrees)"
            value={testShotData.horizontalLaunchAngle}
            onChange={(e) => handleChangeShot('horizontalLaunchAngle', e.target.value)}
          />
          <TextField
            fullWidth={true}
            label="Vertical Launch Angle (Degrees)"
            value={testShotData.verticalLaunchAngle}
            onChange={(e) => handleChangeShot('verticalLaunchAngle', e.target.value)}
          />
          <TextField
            fullWidth={true}
            label="Spin Speed (RPM)"
            value={testShotData.spinSpeed}
            onChange={(e) => handleChangeShot('spinSpeed', e.target.value)}
          />
          <TextField
            fullWidth={true}
            label="Spin Axis (Degrees)"
            value={testShotData.spinAxis}
            onChange={(e) => handleChangeShot('spinAxis', e.target.value)}
          />
          <Button onClick={handleTestShot} fullWidth={true}>Send Shot</Button>
        </Stack>
      </Drawer>
      <Main open={settingsDrawerOpen}>
        <Box>
          <iframe
            ref={frameRef}
            // onLoad={handleFrameLoaded}
            title="fuse"
            style={{
              border: 'none',
              width: '100%',
              height: '100vh',
              backgroundColor: '#000'
            }}
          ></iframe>
        </Box>
      </Main>
    </Box>
  );
}
